import { Banner } from '@mm/components';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { selectPromoterById } from '@mm/state/selectors/promoterSelector';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { pageEntityByIdSelector } from '@mm/state/selectors/pageSelectors';
import { useResolutionInfo } from '@mm/hooks/src/use-resolution-info';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import instagramEngagementsBanner from 'assets/instagram-catch-all-engagements-slideout-banner.png';
import facebookEngagementsBanner from 'assets/facebook-catch-all-engagements-slideout-banner.png';
import { EmbeddableDialogueEditorWithPreview } from '../../../../features/startBotPromoters/common/StartBotsDialogueEditor/EmbeddableDialogueEditorWithPreview';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import { PromoterHeader } from '../../components/PromoterHeader/PromoterHeader';
import { MOBILE_THRESHOLD } from '../../../../constants/screenSize';
import { useSupportedWidgets } from '../../../../../common/components/AddWidgetInsert/AddWidgetInsert';
import { IG_SUPPORTED_WIDGET_TYPES } from '../../../../features/startBotPromoters/instagramStoryMention/InstagramStoryMentionEditor';
import styles from './EngagementSlideoutEditor.module.css';
import CollapsibleContentContainer from '../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainer';
import FrequencyCapControl from '../../../../features/startBotPromoters/commentGuards/components/CommentGuardForm/FrequencyCapControl';
import { VIPs } from '../../../../features/configure/panels/sections/BotSettingsSection/VIPs/vips';
import { DialoguePreview } from '../../../../../modules/DailoguePreview/DialoguePreview';
import { FreeTrialWarning } from '../../elements/FreeTrialWarning';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';

export interface EngagementsSlideoutEditorProps {
  promoterId: number;
  platform: 'facebook' | 'instagram';
}

export const EngagementSlideoutEditor = ({
  promoterId,
  platform,
}: EngagementsSlideoutEditorProps) => {
  const promoter = useSelector(state => selectPromoterById(state, promoterId));
  const promoterCRUDActions = usePromoterCRUDActions();
  const chatbotId = useSelector(activeChatbotIdSelector);
  const page = useSelector(state =>
    pageEntityByIdSelector(state, String(promoter?.pageId)),
  );
  const [imageLoading, setImageLoading] = useState(false);
  const { screenWidth } = useResolutionInfo();
  const { setRef } = useSlideoutOnClose();
  const isMobile = screenWidth <= MOBILE_THRESHOLD;
  const forInstagram = platform === 'instagram';
  const platformName = forInstagram ? 'Instagram ' : 'Facebook ';

  const getBannerProp = () => {
    if (forInstagram) {
      return {
        img: instagramEngagementsBanner,
        css: styles.instagramEngagementsBanner,
      };
    }
    return {
      img: facebookEngagementsBanner,
      css: styles.facebookEngagementsBanner,
    };
  };
  const { allWidgets: allSupportedWidgets } = useSupportedWidgets();
  const widgetsToExclude = allSupportedWidgets
    .filter(w => !IG_SUPPORTED_WIDGET_TYPES.includes(w.type))
    .map(w => w.type);

  useEffect(() => {
    const automationKind = forInstagram
      ? 'instagram_catch_all'
      : 'facebook_catch_all';
    async function fetchItems() {
      await promoterCRUDActions.retrieveAll(chatbotId, automationKind);
    }
    fetchItems();
  }, [chatbotId, promoterCRUDActions]);

  if (!promoter || !page) {
    return null;
  }

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <PromoterHeader
        promoter={promoter}
        title={`${platformName} Engagement Autoresponder`}
        hideDeleteAction
      />
      <FreeTrialWarning />

      <SlideoutMenu.Content
        className={classNames(
          styles.content,
          forInstagram ? styles.contentInstagram : styles.contentFacebook,
        )}
      >
        {!isMobile && (
          <Banner
            css={{
              container: getBannerProp().css,
              header: getBannerProp().css,
              collapsedHeader: styles.collapsedHeader,
              content: styles.bannerContent,
            }}
            contentLoading={imageLoading}
            setContentLoading={setImageLoading}
            withBannerHeader
            showCloseIcon
            usedInSlideout
            headerText={`What is ${platformName} Engagement Autoresponder?`}
          >
            <img
              src={getBannerProp().img}
              onLoad={() => setImageLoading(false)}
            />
          </Banner>
        )}
        <div className={styles.container}>
          <div className={styles.configArea}>
            <CollapsibleContentContainer
              orderNumber="1"
              containerName={`Reply instantly to users who engaged on ${platformName}`}
              className={styles.collapsibleContainer}
              stepperStylingPlatform={forInstagram ? 'instagram' : 'facebook'}
              useCircleNumberStyle
            >
              <EmbeddableDialogueEditorWithPreview
                editPaneClassname={styles.dialogueEditPane}
                previewClassName={styles.dialogueEditPreviewPane}
                dialogueEditorClassName={styles.dialogueContainer}
                dialogueId={page.id}
                widgetsToExclude={forInstagram ? widgetsToExclude : []}
                platforms={forInstagram ? ['instagram'] : ['facebook']}
                usedInSlideout
                hideDialogueSelector
                showAttributesStartWarning={forInstagram}
                forInstagram={forInstagram}
              />
            </CollapsibleContentContainer>
            <CollapsibleContentContainer
              orderNumber="2"
              containerName="Specify special sending rules"
              className={styles.collapsibleContainer}
              stepperStylingPlatform={forInstagram ? 'instagram' : 'facebook'}
              useCircleNumberStyle
            >
              {forInstagram && (
                <VIPs
                  withTitle={false}
                  className={styles.vip}
                  headingSize={5}
                  headingClassName={styles.vipHeading}
                />
              )}
              <FrequencyCapControl
                guard={promoter}
                chatbotId={chatbotId}
                css={{ label: styles.frequencyCapLabel }}
                usedInSlideout
              />
            </CollapsibleContentContainer>
          </div>
          {!isMobile && (
            <DialoguePreview
              dialogueId={page.id}
              className={styles.dialoguePreview}
              platforms={forInstagram ? ['instagram'] : ['facebook']}
              sticky
            />
          )}
        </div>
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
